// Utility functions for encryption
export const EncryptionUtils = {
    // Formata a chave para garantir tamanho compatível com AES-GCM
    formatKey: (key: string): string => 
        key.padEnd(32, "0").slice(0, 32),
    
    // Cria um hash da senha usando o algoritmo SHA-256 para fins de verificação
    async createHash(text: string): Promise<string> {
        const encoder = new TextEncoder();
        const data = encoder.encode(text);
        const hashBuffer = await crypto.subtle.digest('SHA-256', data);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
        return hashHex;
    },

    // Encripta dados com uma chave secreta, usando AES-GCM
    async encrypt(text: string, secretKey: string) {
        try {
            // Validar entrada
            if (!text || !secretKey) {
                throw new Error("Texto e chave secreta são obrigatórios");
            }
            
            const formattedKey = this.formatKey(secretKey);
            // Vetor de inicialização aleatório para cada criptografia
            const iv = crypto.getRandomValues(new Uint8Array(12));
            const encoder = new TextEncoder();
            
            // Importa a chave
            const key = await crypto.subtle.importKey(
                "raw",
                encoder.encode(formattedKey),
                { name: "AES-GCM" },
                false,
                ["encrypt"]
            );

            // Adiciona um timestamp para verificação de validade
            const timestamp = Date.now();
            const dataToEncrypt = JSON.stringify({
                data: text,
                timestamp
            });

            // Criptografa os dados
            const encrypted = await crypto.subtle.encrypt(
                { name: "AES-GCM", iv },
                key,
                encoder.encode(dataToEncrypt)
            );

            // Retorna objeto com vetor de inicialização e conteúdo criptografado
            return {
                iv: Array.from(iv),
                content: Array.from(new Uint8Array(encrypted)),
                version: 1  // Versão do algoritmo para facilitar migrações futuras
            };
        } catch (error) {
            console.error("Erro na criptografia:", error);
            throw error;
        }
    },

    // Descriptografa dados com uma chave secreta
    async decrypt(encryptedData: { iv: number[], content: number[], version?: number }, secretKey: string) {
        try {
            // Validar entrada
            if (!encryptedData || !secretKey) {
                throw new Error("Dados criptografados e chave secreta são obrigatórios");
            }
            
            const { iv, content, version = 1 } = encryptedData;
            
            // Verifica a versão para compatibilidade
            if (version !== 1) {
                console.warn("Versão de criptografia não reconhecida. Tentando descriptografar mesmo assim.");
            }
            
            const formattedKey = this.formatKey(secretKey);
            const decoder = new TextDecoder();
            
            // Importa a chave
            const key = await crypto.subtle.importKey(
                "raw",
                new TextEncoder().encode(formattedKey),
                { name: "AES-GCM" },
                false,
                ["decrypt"]
            );

            // Descriptografa
            const decrypted = await crypto.subtle.decrypt(
                { name: "AES-GCM", iv: new Uint8Array(iv) },
                key,
                new Uint8Array(content)
            );

            // Extrai os dados e timestamp
            const decryptedText = decoder.decode(decrypted);
            const { data, timestamp } = JSON.parse(decryptedText);
            
            // Verifica se o dado não está expirado (opcional, dependendo da política)
            const maxAgeMs = 30 * 24 * 60 * 60 * 1000; // 30 dias em milissegundos
            const now = Date.now();
            if (now - timestamp > maxAgeMs) {
                console.warn("Dados descriptografados podem estar obsoletos");
            }
            
            return data;
        } catch (error) {
            console.error("Erro na descriptografia:", error);
            throw error;
        }
    },

    // Tenta descriptografar com várias chaves, para suportar rotação de chaves
    async tryDecryptWithKeys(encryptedData: any, primaryKey: string, fallbackKeys: string[] = []): Promise<string | null> {
        try {
            // Primeiro tenta com a chave primária
            return await this.decrypt(encryptedData, primaryKey);
        } catch (error) {
            // Se falhar, tenta com as chaves de fallback em sequência
            for (const key of fallbackKeys) {
                try {
                    return await this.decrypt(encryptedData, key);
                } catch (innerError) {
                    // Continua para a próxima chave
                }
            }
            
            // Se todas as tentativas falharem
            console.error("Não foi possível descriptografar com nenhuma das chaves disponíveis");
            return null;
        }
    }
};